import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Checkbox } from '@els/els-react--checkbox';
import { SingleSelect } from '@els/els-react--select';
import { RawAutosuggest as Autosuggest } from '@els/els-react--autosuggest';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';
import { LinkElement } from '@els/els-react--link-element';
import { Banner } from '@els/els-react--banner';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import InnerHTML from '@Components/InnerHTML';
import { allowedTagsDefault } from '@Services/HtmlSanitizeService';
import LoginFormHeader from '../../Components/LoginFormHeader';

import './InstitutionForm.scss';
import '../../Components/LoginForm.scss';
import { paths } from '../../../../AppRoutes/constants';

function InstitutionForm({
  cancel,
  actualData,
  selectGroup,
  login,
  institutionsGroups,
  institutionsForSearch,
  goToLastSelected,
  ipAddress,
  isAnonymousUser,
  selectInstitution,
  selectSearchedInstitution,
  lastSelectedInstitution,
  changeRememberMe,
  loginFail,
  isLoading,
  institutionErrorMessage,
  notifications,
  onEmailSignIn,
  onRegister,
}) {
  const [searchForInstitution, setSearchForInstitution] = useState('');
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const history = useHistory();
  const { currentInstitutions, group, institution, rememberChoice } = actualData;
  const onChangeRememberMe = (e) => changeRememberMe(e.target?.checked);

  const mapDropdownItems = (items) => {
    if (!items || !items.length) {
      return [];
    }
    return items.map((item, index) => ({
      label: item.description,
      value: `${item.description}-${index}`,
    }));
  };

  const getData = () => {
    if (!institutionsForSearch || !institutionsForSearch.length) {
      return [];
    }
    const data = institutionsForSearch.filter((item) => {
      const description = item.description?.toLowerCase();
      return description.includes(searchForInstitution.toLowerCase());
    });

    return data
      .map((item) => ({
        inputValue: item.description,
        displayValue: item.description,
      }))
      .slice(0, 5);
  };

  const mappedInstitutionsGroups = mapDropdownItems(institutionsGroups);
  const mappedCurrentInstitutions = mapDropdownItems(currentInstitutions);
  const matchingInstitutions = getData();
  const matchingCount = matchingInstitutions.length;
  const matchingNames = matchingInstitutions.map((item) => item.displayValue).join(', ');
  const searchSuggestionStatusMessage =
    searchForInstitution.trim() && matchingCount > 0
      ? `${matchingCount} matching institution available for ${searchForInstitution.trim()}: ${matchingNames}.`
      : `No matches found for ${searchForInstitution.trim()}.`;

  const onSelectInstitutionGroup = ({ value }) => {
    if (!institutionsGroups || !institutionsGroups.length) {
      return;
    }
    const lastDashIndex = value.lastIndexOf('-');
    const index = lastDashIndex >= 0 && parseInt(value.substring(lastDashIndex + 1), 10);
    const selectedGroup = institutionsGroups[index];
    selectGroup(selectedGroup);
  };

  const onSelectInstitution = ({ value }) => {
    if (!currentInstitutions || !currentInstitutions.length) {
      return;
    }
    const lastDashIndex = value.lastIndexOf('-');
    const index = lastDashIndex >= 0 && parseInt(value.substring(lastDashIndex + 1), 10);
    const selectedInstitution = currentInstitutions[index];
    selectInstitution(selectedInstitution);
  };

  const onSelectSearchedInstitution = (selectedInstitution) => {
    if (!institutionsForSearch || !institutionsForSearch.length) {
      return;
    }
    const selectedSearchedInstitution = institutionsForSearch.find((item) => item.description === selectedInstitution.value);
    selectSearchedInstitution(selectedSearchedInstitution);
  };

  const onLastSelectedInstitutionClick = () => {
    goToLastSelected(lastSelectedInstitution);
  };

  return (
    <div className="login-form institutional-form login-page-form-wrapper">
      <LoginFormHeader
        cancel={cancel}
        isAnonymousUser={isAnonymousUser}
        ipAddress={ipAddress}
        actionMessage="Institution sign in"
        infoMessage={
          <>
            Select your institution name from the list.
            <br />
            Once authenticated you will be sent back to Reaxys.
          </>
        }
      />
      {notifications &&
        isBannerVisible &&
        (notifications.CUSTOM_LEFT?.length > 0 || notifications.CUSTOM_RIGHT?.length > 0 || notifications.INSTITUTIONAL_GREETING) && (
          <div className="institutional-form__search-toggle u-els-margin-top u-els-padding-bottom">
            <Banner isVisible size="small" onClose={() => setIsBannerVisible(false)} hideCloseLabel>
              {notifications.CUSTOM_LEFT?.map((notification) => (
                <InnerHTML
                  key={`left-${notification}`}
                  tag="div"
                  html={notification}
                  mode="escape"
                  allowedTags={allowedTagsDefault.concat('a')}
                  allowedAttributes={['href', 'target']}
                />
              ))}
              {notifications.CUSTOM_RIGHT?.map((notification) => (
                <InnerHTML
                  key={`right-${notification}`}
                  tag="div"
                  html={notification}
                  mode="escape"
                  allowedTags={allowedTagsDefault.concat('a')}
                  allowedAttributes={['href', 'target']}
                />
              ))}
              {notifications.INSTITUTIONAL_GREETING && (
                <InnerHTML
                  tag="div"
                  html={notifications.INSTITUTIONAL_GREETING}
                  mode="escape"
                  allowedTags={allowedTagsDefault.concat('a')}
                  allowedAttributes={['href', 'target']}
                />
              )}
            </Banner>
          </div>
        )}
      <TogglePanelCollection
        headerLevel="2"
        isAccordion
        size="m"
        noTopKeyline
        noIndent
        className="institutional-form__toggle-panels"
        data-e2e="institution-toggle-panels-collection">
        <TogglePanel id="search-for-institution" header="Search for your institution" data-e2e="institution-search-toggle-panel">
          <div className="institutional-form__search-input" id="els-input-wrapper">
            {lastSelectedInstitution && (
              <div className="institutional-form__last-selected-institution u-els-padding-left-2x u-els-padding-bottom">
                <span>Previously selected: </span>
                <LinkElement
                  data-e2e="institution-last-selected"
                  isExternal
                  inline
                  noExternalIcon
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onLastSelectedInstitutionClick();
                  }}>
                  {lastSelectedInstitution.institution}
                </LinkElement>
              </div>
            )}
            <div className="institutional-form__search-input-inner">
              <Autosuggest
                textInputProps={{
                  label: 'Institution name',
                  placeholder: 'Enter your institution name',
                  required: true,
                }}
                suggestions={matchingInstitutions}
                onChange={(value) => setSearchForInstitution(value)}
                onSelectSuggestion={(value) => {
                  const selectedItem = institutionsForSearch.find((item) => item.description === value);
                  if (selectedItem) {
                    onSelectSearchedInstitution({ value: selectedItem.description });
                  }
                }}
                shouldAlwaysChooseSuggestion={false}
                filterSuggestions
                className="institutional-form__autosuggest"
              />
              <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                {searchSuggestionStatusMessage}
              </div>
            </div>
          </div>
        </TogglePanel>
        <TogglePanel id="select-institution" header="Choose institution’s region or group" data-e2e="institution-select-toggle-panel">
          <SingleSelect
            key={`institution-group-${group || 'none'}`}
            id="institution-group-dropdown"
            className="e2e-institution-group-dropdown institutional-form__search-input-inner"
            data-e2e="institution-group-dropdown"
            options={mappedInstitutionsGroups}
            selectedOptions={group ? [`${group}-${institutionsGroups.findIndex((item) => item.description === group)}`] : undefined}
            label="Region or group"
            optionsFlyoutProps={{
              maxHeight: '10rem',
            }}
            placeholderText="Select your region or group"
            onSelect={onSelectInstitutionGroup}
            required
          />
          <div className="institutional-form__dropdown u-els-margin-top">
            <SingleSelect
              key={`institution-${institution || 'none'}`}
              id="institution-institution-dropdown"
              className="e2e-institution-institution-dropdown institutional-form__search-input-inner"
              options={mappedCurrentInstitutions}
              data-e2e="institution-institution-dropdown"
              optionsFlyoutProps={{
                maxHeight: '10rem',
              }}
              selectedOptions={
                institution ? [`${institution}-${currentInstitutions.findIndex((item) => item.description === institution)}`] : undefined
              }
              label="Institution"
              placeholderText="Select your institution"
              onSelect={onSelectInstitution}
              required
              isDisabled={!(currentInstitutions && currentInstitutions.length)}
            />
          </div>
        </TogglePanel>
      </TogglePanelCollection>

      {!isLoading && loginFail && <div className="login-form__content_error u-els-margin-bottom">{institutionErrorMessage}</div>}

      <div className="els-text-input">
        <div className="login-form__content_remember institutional-form__remember">
          <Checkbox
            checked={rememberChoice}
            id="institution_remember"
            data-e2e="institution-remember-checkbox"
            aria-checked={rememberChoice}
            onChange={onChangeRememberMe}
            label="Stay signed in (not recommended for shared devices)"
          />
        </div>
      </div>

      <div className="login-form__content_actions">
        <div className="login-form__content_actions-button">
          <Button
            type="primary"
            data-e2e="institution-sign-in"
            onClick={login}
            disabled={!(currentInstitutions && currentInstitutions.length)}
            className="e2e-institution-sign-in institutional-form__button-full-width">
            Submit and continue
          </Button>
        </div>
      </div>
      <div className="login-form__message login-divider u-els-margin-top-1o2">
        <div className="separator-line" />
        <div className="separator-content u-els-margin-left-1o2">Or continue with:</div>
        <div className="separator-line u-els-margin-left-1o2" />
      </div>
      <div className="login-form__message">
        <ButtonWithIcon
          type="tertiary"
          sprite="Email"
          name="Email sign in"
          data-e2e="email-sign-in-button"
          onClick={onEmailSignIn || (() => history.push(paths.LOGIN))}
          expanded
          className="u-els-margin-top-1x1o2 e2e-login-sign-in">
          Email sign in
        </ButtonWithIcon>
      </div>
      <span
        className="login-form__message signup-trouble u-els-margin-top-1x1o2"
        data-click-delegation
        data-tracking="true"
        data-tracking-type="productfeatureused"
        data-tracking-action="not registered">
        <span>Don&apos;t have an account?</span>
        <LinkElement
          className="u-els-padding-left-1o4"
          data-e2e="register-link"
          inline
          isExternal
          noExternalIcon
          iconEmphasis
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (onRegister) {
              onRegister();
            } else {
              history.push(paths.PRIVATE_REGISTER);
            }
          }}>
          Register
        </LinkElement>
      </span>
      <div className="login-form__message u-els-margin-top-1x1o2">
        <LinkElement
          isExternal
          data-e2e="privacy-policy-link"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Privacy Policy
        </LinkElement>
        <LinkElement
          isExternal
          data-e2e="terms-and-conditions-link"
          className="u-els-margin-left"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Terms and Conditions
        </LinkElement>
      </div>
    </div>
  );
}

InstitutionForm.defaultProps = {
  actualData: {},
  institutionsGroups: [],
  institutionsForSearch: [],
};

InstitutionForm.propTypes = {
  isAnonymousUser: PropTypes.bool,
  cancel: PropTypes.func.isRequired,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.string,
  actualData: PropTypes.object,
  selectGroup: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  institutionsGroups: PropTypes.array,
  institutionsForSearch: PropTypes.array,
  goToLastSelected: PropTypes.func.isRequired,
  selectInstitution: PropTypes.func.isRequired,
  selectSearchedInstitution: PropTypes.func.isRequired,
  lastSelectedInstitution: PropTypes.object,
  changeRememberMe: PropTypes.func.isRequired,
  institutionErrorMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  notifications: PropTypes.object,
  onEmailSignIn: PropTypes.func,
  onRegister: PropTypes.func,
};

export default InstitutionForm;
